/**
 * Reads email-verification state off the ID token (THLE-4186).
 *
 * The claim is `custom:emailVerified`, added by the pre-token-generation Lambda
 * (DX-4612) as the string `'true'`/`'false'`. Not the standard OIDC
 * `email_verified`, which TH tokens omit entirely — the Cognito attribute behind
 * it is set to `True` at account creation, so it records email delivery rather
 * than whether the guest ever followed a verification link. th-app gates on the
 * same custom claim (THLE-4231).
 *
 * Read from the token rather than `me.details.emailVerified` because the backend
 * gates on the same token: a since-verified profile would pass here only to be
 * refused off the still-stale token.
 *
 * SECURITY: a browser-decoded JWT is forgeable, so this drives presentation
 * only. `EMAIL_VERIFICATION_REQUIRED` from the gateway is the real gate.
 */

import { parseJwt } from 'utils/jwt';

const EMAIL_VERIFIED_CLAIM = 'custom:emailVerified';

/**
 * `null` means unknown — callers must fail open and let the backend decide.
 * A string is what the Lambda emits; a JSON boolean is accepted too, so the
 * gate survives the claim ever being normalized at the source.
 */
export function getEmailVerifiedFromClaims(
  claims: Record<string, unknown> | null | undefined
): boolean | null {
  const claim = claims?.[EMAIL_VERIFIED_CLAIM];
  if (typeof claim === 'boolean') {
    return claim;
  }
  if (claim === 'true' || claim === 'false') {
    return claim === 'true';
  }
  return null;
}

/** As above, for a raw JWT string such as the app handoff token. */
export function getEmailVerified(idToken: string | null): boolean | null {
  if (!idToken) {
    return null;
  }
  return getEmailVerifiedFromClaims(parseJwt(idToken));
}
